import React from 'react';
import { Box, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';

function PrivacyPolicy() {
    return (
        <>
            <Helmet>
                <title>Xpanse Cafe Privacy Policy – Your Data, Our Responsibility</title>
                <meta name="description" content="Read how Xpanse Cafe collects, protects, and uses your personal data when you order online or via our app. We prioritize your privacy and security." />
                <meta property="og:title" content="Xpanse Privacy Policy" />
                <meta property="og:description" content="Read how Xpanse Cafe collects, protects, and uses your personal data when you order online or via our app. We prioritize your privacy and security." />
                <meta property="og:url" content="https://xpansecafe.com/privacyPolicy" />
                <meta property="og:type" content="website" />
                <link rel="canonical" href="https://xpansecafe.com/privacyPolicy" />
            </Helmet>
            <div className='privacyScreen'>
                <Typography className='privacyTitle'>PRIVACY POLICY</Typography>
                <Box className='privacyPolicyContainer'>
                    <div>
                        <Typography className='lastUpdatedText'>EFFECTIVE DATE: 23 April 2025</Typography>
                        <p className='privacyPolicyDesc'>Xpanse (“Xpanse,” “we,” “us,” or “our”), operated by TEAM24 RETAIL TECH PRIVATE LIMITED, with its registered address at House No. 13/279/A70 T-11, Shed No Silvio Heights, Panaji, Tiswadi, North Goa - 403001, Goa, India, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our mobile application, website (including subdomains and microsites), and physical stores. By using our services, you agree to the terms of this Privacy Policy.</p>
                    </div>

                    <div>
                        <Typography className='aboutUsText'>DEFINITIONS</Typography>
                        <p className='privacyPolicyDesc'>Data: Includes personal information, sensitive personal information, and non-personal information about you that, directly or indirectly, could identify you.</p>
                        <p className='privacyPolicyDesc'>Personal Information: Information that identifies you directly, such as your name, email address, and phone number.</p>
                        <p className='privacyPolicyDesc'>Sensitive Personal Information: Includes data revealing age, sex, date of birth, marital status, nationality, occupation, travel history or any other personal information provided in responses to surveys or questionnaires.</p>
                        <p className='privacyPolicyDesc'>Cookies: Small files placed on your device by our website or mobile application that allow a website to remember your actions or preferences over time.</p>
                        <p className='privacyPolicyDesc'>Partners: Third-party businesses with whom we have contracts to provide services.</p>
                        <p className='privacyPolicyDesc'>Service Providers: Entities that process data on our behalf under a written contract.</p>
                        <p className='privacyPolicyDesc'>User: Any individual who accesses our stores, website, or mobile application.</p>
                    </div>

                    <div>
                        <Typography className='purposeText'>DATA WE COLLECT</Typography>
                        <p className='privacyPolicyDesc'>We collect the following categories of data:
                            <ul>
                                <li>Contact Information: First and last name, email address, postal address, country, and phone number.</li>
                                <li>Financial Information: Payment instrument information (processed securely by our payment gateway), transaction history, and payment method.</li>
                                <li>Technical Information: IP address, device information, website/app usage data, cookies.</li>
                                <li>Transaction Information: Date, amount, and details of transactions.</li>
                                <li>Account Information: Account membership number and registration information.</li>
                            </ul>
                            We do not collect marital status, religion, ethnicity, or other unnecessary personal information.
                        </p>
                    </div>

                    <div>
                        <Typography className='consentText'>HOW WE COLLECT DATA</Typography>
                        <p className='privacyPolicyDesc'>
                            <ul>
                                <li>Information You Provide: Information directly submitted through our website, app, or in-store.</li>
                                <li>Automatic Data Collection: Through cookies, pixels, and similar technologies.</li>
                                <li>Third-Party Sources: Data received from third parties with your consent (e.g., social media platforms).</li>
                            </ul>
                        </p>
                    </div>

                    <div>
                        <Typography className='consentText'>USE OF DATA</Typography>
                        <p className='privacyPolicyDesc'>
                            We use your data to:
                            <ul>
                                <li>Fulfill orders and provide services</li>
                                <li>Provide personalized offers and recommendations</li>
                                <li>Communicate about your orders and our services</li>
                                <li>Improve website/app experience</li>
                                <li>Analyze usage, conduct analytics, and prevent fraud</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </p>
                    </div>

                    <div>
                        <Typography className='consentText'>DATA SHARING</Typography>
                        <p className='privacyPolicyDesc'>
                            <ul>
                                <li>Partners: Only with your explicit consent, and for specific service-related purposes.</li>
                                <li>Service Providers: Under strict contractual obligations to maintain data confidentiality and comply with data protection laws.</li>
                            </ul>
                        </p>
                    </div>

                    <div>
                        <Typography className='consentText'>DATA SECURITY</Typography>
                        <p className='privacyPolicyDesc'>We implement technical and organizational measures like encryption, access controls, and security audits to protect your data. We have a breach response plan for incidents.</p>
                    </div>

                    <div>
                        <Typography className='consentText'>DATA RETENTION</Typography>
                        <p className='privacyPolicyDesc'>We retain your data only for as long as needed to fulfill the purposes outlined in this Privacy Policy or as required by law.</p>
                    </div>

                    <div>
                        <Typography className='consentText'>LOCATION DATA</Typography>
                        <p className='privacyPolicyDesc'>
                            To enhance your user experience, we may request access to your device’s location to help identify the nearest Xpanse Café outlets and services available in your area.
                            We may request permission to access your device’s approximate location using ACCESS_COARSE_LOCATION, which allows us to determine your location within approximately 11 kilometers. This data is used solely to check your real-time proximity to nearby stores and to enhance location-based features within the app.
                            Your location is used only at the time of request and is neither stored nor tracked continuously. It is not linked to your identity, and we do not share this data with any third parties or advertisers. We do not collect your precise (fine) location and we do not track your location in the background. Additionally, location data is not retained or reused beyond its immediate functional purpose. You retain full control over location sharing and may enable or disable location permissions at any time through your device settings.
                        </p>
                    </div>

                    <div>
                        <Typography className='consentText'>YOUR RIGHTS AND CHOICES</Typography>
                        <p className='privacyPolicyDesc'>You may have rights to:
                            <ul>
                                <li>Confirm data processing and request access</li>
                                <li>Request correction or updates</li>
                                <li>Restrict or object to use</li>
                                <li>Withdraw consent</li>
                                <li>File complaints</li>
                                <li>Opt out of marketing communications</li>
                            </ul>
                            Contact us to exercise these rights.
                        </p>
                    </div>

                    <div>
                        <Typography className='consentText'>CHILDREN’S PRIVACY</Typography>
                        <p className='privacyPolicyDesc'>Our services are not intended for individuals under 18. We do not knowingly collect personal information from minors.</p>
                    </div>

                    <div>
                        <Typography className='consentText'>CHANGES TO THIS POLICY</Typography>
                        <p className='privacyPolicyDesc'>We may update this Privacy Policy from time to time. Updated versions will be posted on our website. Continued use after updates implies acceptance.</p>
                    </div>

                    <div>
                        <Typography className='consentText'>LEGAL COMPLIANCE</Typography>
                        <p className='privacyPolicyDesc'>We comply with applicable laws, including sections of the Information Technology Act, to protect your sensitive personal data.</p>
                    </div>

                    <div>
                        <Typography className='consentText'>ACCOUNT DELETION</Typography>
                        <p className='privacyPolicyDesc'>Users can delete their account and all associated information at <a href='https://xpansecafe.com/deleteAccount' target='_blank'>https://xpansecafe.com/deleteAccount</a></p>
                    </div>

                    <div>
                        <Typography className='consentText'>COOKIES AND TRACKING TECHNOLOGIES</Typography>
                        <p className='privacyPolicyDesc'>Our website and app use cookies and similar technologies to improve your experience. You can manage preferences via browser settings.</p>
                    </div>

                    <div>
                        <Typography className='consentText'>CONTACT INFORMATION</Typography>
                        <p className='privacyPolicyDesc'>For questions or concerns about this Privacy Policy, contact us at:
                            <br />Email: contact@xpansecafe.com
                            <br />Address: House No. 13/279/A70 T-11, Silvio Heights, Panaji, Tiswadi, North Goa - 403001, Goa, India
                        </p>
                    </div>
                </Box>
            </div>
        </>
    );
}

export default PrivacyPolicy;
